import React, { useState, useEffect } from "react";
import { getApiBaseUrl } from "../../services/apiBase";

const fmtQtd = (v) => Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 3 });
const num = (v) => parseFloat(v) || 0;

export default function CotacaoFornecedorPublic() {
  const params = new URLSearchParams(window.location.search);
  const tokenUrl = params.get("token") || "";

  const [token, setToken] = useState(tokenUrl);
  const [dados, setDados] = useState(null);
  const [linhas, setLinhas] = useState([]);
  const [loading, setLoading] = useState(!!tokenUrl);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  const BASE = getApiBaseUrl();

  const carregar = async (tk) => {
    if (!tk || tk.length < 16) {
      setErro("Informe o código de acesso recebido por e-mail.");
      setLoading(false);
      return;
    }
    setLoading(true); setErro(null); setOkMsg(null);
    try {
      const r = await fetch(`${BASE}/cotacao/publico/${encodeURIComponent(tk)}`);
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.message || "Convite inválido.");
      setDados(data.data);
      setLinhas((data.data.itens || []).map((i) => ({
        idCotacaoItem: i.id,
        cdItem: i.cdItem,
        nmItem: i.nmItem,
        quantidade: i.quantidade,
        siglaUm: i.siglaUm,
        disponivel: true,
        vlrUnitario: "",
        prazoEntrega: "",
        observacao: "",
      })));
    } catch (e) { setErro(e.message); setDados(null); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (tokenUrl) carregar(tokenUrl);
  }, [tokenUrl]);

  const enviar = async () => {
    setSaving(true); setErro(null);
    try {
      const r = await fetch(`${BASE}/cotacao/publico/${encodeURIComponent(token)}/responder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          linhas: linhas.map((l) => ({
            idCotacaoItem: l.idCotacaoItem,
            disponivel: l.disponivel ? 1 : 0,
            vlrUnitario: l.disponivel && l.vlrUnitario !== "" ? num(l.vlrUnitario) : null,
            prazoEntrega: l.disponivel ? (l.prazoEntrega || null) : null,
            observacao: l.observacao || null,
          })),
        }),
      });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.message || "Erro ao enviar.");
      setOkMsg(data.message || "Proposta enviada.");
      setDados((p) => (p ? { ...p, jaRespondeu: true } : p));
    } catch (e) { setErro(e.message); }
    finally { setSaving(false); }
  };

  if (!dados && !loading && !tokenUrl) {
    return (
      <div className="container py-5" style={{ maxWidth: 480 }}>
        <h4 className="mb-3">Cotação — fornecedor</h4>
        <p className="text-muted small">Informe o código recebido por e-mail para acessar a cotação.</p>
        <input className="form-control mb-2" value={token}
          onChange={(e) => setToken(e.target.value)} placeholder="Código de acesso" />
        <button type="button" className="btn btn-primary" onClick={() => carregar(token)}>
          Acessar
        </button>
        {erro && <div className="alert alert-danger mt-3 small">{erro}</div>}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-5 text-muted">Carregando cotação…</div>
    );
  }

  if (erro && !dados) {
    return (
      <div className="container py-5 text-center" style={{ maxWidth: 480 }}>
        <div className="text-danger mb-2">{erro}</div>
        <button type="button" className="btn btn-outline-secondary btn-sm"
          onClick={() => { setErro(null); setDados(null); setToken(""); }}>
          Tentar outro código
        </button>
      </div>
    );
  }

  if (!dados) return null;

  const readOnly = dados.jaRespondeu || okMsg;

  return (
    <div className="container py-4" style={{ maxWidth: 900 }}>
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <h4 className="mb-1">{dados.titulo}</h4>
          <p className="text-muted small mb-2">{dados.nmCondominio}</p>
          {dados.descricao && <p className="small">{dados.descricao}</p>}
          <p className="small">
            Fornecedor: <strong>{dados.nmFornecedor}</strong>
            {dados.dtLimite && (
              <span className="ms-2 text-muted">
                Prazo: {new Date(`${dados.dtLimite.split("T")[0]}T12:00:00`).toLocaleDateString("pt-BR")}
              </span>
            )}
          </p>
          {okMsg && <div className="alert alert-success small">{okMsg}</div>}
          {dados.jaRespondeu && !okMsg && (
            <div className="alert alert-info small">Proposta já registrada. Obrigado.</div>
          )}

          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Item</th><th>Qtd</th>
                  <th className="text-end">Preço un.</th><th>Entrega</th><th>Obs.</th><th>Disp.</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, idx) => (
                  <tr key={l.idCotacaoItem}>
                    <td><code className="small">{l.cdItem}</code> {l.nmItem}</td>
                    <td>{fmtQtd(l.quantidade)} {l.siglaUm}</td>
                    <td>
                      <input type="number" step="0.0001" className="form-control form-control-sm text-end"
                        disabled={readOnly || !l.disponivel} value={l.vlrUnitario}
                        onChange={(e) => setLinhas((p) => p.map((x, i) => (
                          i === idx ? { ...x, vlrUnitario: e.target.value } : x
                        )))} />
                    </td>
                    <td>
                      <input type="date" className="form-control form-control-sm"
                        disabled={readOnly || !l.disponivel} value={l.prazoEntrega}
                        onChange={(e) => setLinhas((p) => p.map((x, i) => (
                          i === idx ? { ...x, prazoEntrega: e.target.value } : x
                        )))} />
                    </td>
                    <td>
                      <input className="form-control form-control-sm" disabled={readOnly || !l.disponivel}
                        value={l.observacao}
                        onChange={(e) => setLinhas((p) => p.map((x, i) => (
                          i === idx ? { ...x, observacao: e.target.value } : x
                        )))} />
                    </td>
                    <td className="text-center">
                      <input type="checkbox" className="form-check-input" disabled={readOnly}
                        checked={!!l.disponivel}
                        onChange={(e) => setLinhas((p) => p.map((x, i) => (
                          i === idx ? { ...x, disponivel: e.target.checked } : x
                        )))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!readOnly && (
            <button type="button" className="btn btn-primary" disabled={saving} onClick={enviar}>
              {saving ? "Enviando…" : "Enviar proposta"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
